import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';

import { JobStatus } from '../domain/enums/jobStatus';
import { Ordering } from '../domain/enums/ordering';
import { OverviewType } from '../domain/enums/overviewType';
import { Alert } from '../domain/models/alert';
import { LoginDomainService } from '../domain/services/login/login.domain.service';
import { RouteOverviewService } from './services/route-overview.service';
import { RouteOverviewViewModel } from './viewModels/route-overview.viewModel';

@Component({
  selector: 'app-route-overview',
  templateUrl: './route-overview.component.html',
  styleUrls: ['./route-overview.component.scss']
})
export class RouteOverviewComponent implements OnInit {
  @ViewChild('searchBox') searchBox: ElementRef;

  private routesLoading: boolean;
  private showDeleteRoute: boolean;
  private showStopRoute: boolean;
  private showAlerts: boolean;
  private userId: string;
  private selectedShareLink: string;
  private alerts: Alert[];

  public routes: RouteOverviewViewModel[];
  public currentRoutes: RouteOverviewViewModel[];
  public filteredRoutes: RouteOverviewViewModel[];

  public archivedRoutes: RouteOverviewViewModel[];
  public routeToDelete: RouteOverviewViewModel;
  public routeToStop: RouteOverviewViewModel;
  public selectedRoute: RouteOverviewViewModel;
  public searchTerm: string;

  private activeOverviewType: OverviewType;
  private searchboxActive: boolean;

  private sharePopupShown: boolean;
  private stopSharingPopupShown: boolean;
  public sortOrder: any = {
    Date: Ordering.CreatedDesc,
    Ascending: Ordering.AlphaAsc,
    Descending: Ordering.AlphaDesc
  };

  constructor(private router: Router,
    private routeOverviewService: RouteOverviewService,
    private loginDomainService: LoginDomainService,
    private zone: NgZone) { }

  ngOnInit() {
    this.userId = this.loginDomainService.getUserId();
    this.getRoutes();
    this.getAlertsForUser();

    this.showDeleteRoute = false;
    this.activateMy();
  }

  getDayName(date: Date): string {
    return moment(date).format('dddd').toUpperCase();
  }

  private getRoutes(): void {
    this.routesLoading = true;
    this.routeOverviewService.getRoutes()
      .subscribe((result: RouteOverviewViewModel[]) => {
        this.zone.run(() => {
          this.routes = result;
          this.getArchivedRoutes();
        });
      },
        (error: any) => {
          this.zone.run(() => {
            this.getArchivedRoutes();
          });
        });
  }

  getArchivedRoutes(): void {
    this.routeOverviewService.getArchivedRoutes()
      .subscribe((result: RouteOverviewViewModel[]) => {
        this.zone.run(() => {
          this.archivedRoutes = _.filter(result, (route: RouteOverviewViewModel) => {
            return route.userId === this.userId;
          });
          this.setCurrentRoutes();
          this.routesLoading = false;
        });
      }, (error: any) => {
        this.zone.run(() => {
          this.routesLoading = false;
        });
      });
  }

  private setCurrentRoutes() {
    switch (this.activeOverviewType) {
      case (OverviewType.Shared): {
        this.currentRoutes = this.getCompanyRoutes();
        break;
      }
      case (OverviewType.Archived): {
        this.currentRoutes = this.archivedRoutes;
        break;
      }
      default: {
        this.currentRoutes = this.getOwnRoutes();
        break;
      }
    }

    this.deactivateSearchbox();
  }

  getAlertsForUser(): void {
    this.routeOverviewService.getAlertsForUser()
      .subscribe((alerts: Alert[]) => {
        this.alerts = alerts;
      });
  }

  newRoute(): void {
    this.router.navigate(['auth/new-route']);
  }
  openRoute(route: any): void {
    this.router.navigate(['auth/route-detail', route.id]);
  }
  deleteRoute(route: RouteOverviewViewModel): void {
    this.routeToDelete = route;
    this.showDeleteRoute = true;
  }
  stopRoute(route: RouteOverviewViewModel): void {
    this.routeToStop = route;
    this.showStopRoute = true;
  }

  copyRoute(route: RouteOverviewViewModel): void {
    this.router.navigate(['auth/copy-route', route.id]);
  }

  archiveRoute(route: RouteOverviewViewModel): void {
    this.routeOverviewService.archiveRoute(route).subscribe((result: any) => {
      this.getRoutes();
    });
  }

  isSharing(route: RouteOverviewViewModel): boolean {
    return route.sharingKey !== undefined && route.sharingKey !== null;
  }

  shareRoute(route: RouteOverviewViewModel): void {
    if (route.sharingKey !== undefined && route.sharingKey !== null) {
      this.selectedRoute = route;
      this.selectedShareLink = this.routeOverviewService.getSharingLink(route.sharingKey);
      this.sharePopupShown = true;
    } else {
      this.routeOverviewService.shareRoute(route).subscribe((result: any) => {
        this.selectedRoute = route;
        this.selectedShareLink = result;
        this.sharePopupShown = true;
      });
    }
  }

  unshareRoute(route: RouteOverviewViewModel): void {
    this.selectedRoute = route;
    this.selectedShareLink = this.routeOverviewService.getSharingLink(route.sharingKey);
    this.stopSharingPopupShown = true;
  }

  stopSharing(route: RouteOverviewViewModel): void {
    this.routeOverviewService.unshareRoute(route).subscribe((result: any) => {
      console.log(result);
      this.stopSharingPopupShown = false;
    });
  }

  onCloseSharePopup(): void {
    this.sharePopupShown = false;
    this.selectedShareLink = '';
  }

  onCloseStopSharingPopup(): void {
    this.stopSharingPopupShown = false;
    this.selectedShareLink = '';
  }

  isSharePopupShown(): boolean {
    return this.sharePopupShown;
  }

  isStopSharingPopupShown(): boolean {
    return this.stopSharingPopupShown;
  }

  activateRoute(route: RouteOverviewViewModel): void {
    this.routeOverviewService.archiveRoute(route).subscribe((result: any) => {
      this.getRoutes();
    });
  }

  cancelDeleteRoute(): void {
    this.showDeleteRoute = false;
    this.routeToDelete = undefined;
  }
  onDeleteRoute(): void {
    this.routeOverviewService.deleteRoute(this.routeToDelete).subscribe((result: boolean) => {
      this.showDeleteRoute = false;
      this.routeToDelete = undefined;
      this.getRoutes();
    }, (error: any) => {
      this.showDeleteRoute = false;
      this.routeToDelete = undefined;
    });

    this.activateMy();
  }

  cancelStopRoute(): void {
    this.showStopRoute = false;
    this.routeToStop = undefined;
  }
  onStopRoute(): void {
    this.routeOverviewService.stopRoute(this.routeToStop).subscribe((result: boolean) => {
      this.showStopRoute = false;
      this.routeToStop = undefined;
      this.getRoutes();
    }, (error: any) => {
      // TODO errorHandling
      this.showStopRoute = false;
      this.routeToStop = undefined;
    });
  }

  private getOwnRoutes(): RouteOverviewViewModel[] {
    return _.filter(this.routes, (route: RouteOverviewViewModel) => {
      return route.userId === this.userId;
    });
  }
  private getCompanyRoutes(): RouteOverviewViewModel[] {
    return _.filter(this.routes, (route: RouteOverviewViewModel) => {
      return route.userId !== this.userId;
    });
  }

  routesAreLoading(): boolean {
    return this.routesLoading;
  }
  isDeleteShown(): boolean {
    return this.showDeleteRoute;
  }
  isStopShown(): boolean {
    return this.showStopRoute;
  }

  routeHasAlerts(routeId: string): boolean {
    let alerts = _.find(this.alerts, (alert: Alert) => {
      return alert.jobId === routeId;
    });
    return alerts !== undefined;
  }

  orderRoutesBy(ordering: Ordering): void {
    this.routeOverviewService.setLastOrderMethod(ordering);

    switch (ordering) {
      case Ordering.CreatedDesc:
        this.currentRoutes = _.orderBy(this.currentRoutes, ['createdDate'], ['desc']);
        break;
      case Ordering.AlphaDesc:
        this.currentRoutes = _.orderBy(this.currentRoutes, ['name'], ['desc']);
        break;
      default:
        this.currentRoutes = _.orderBy(this.currentRoutes, ['name'], ['asc']);
        break;
    }
    this.filteredRoutes = this.currentRoutes;
  }

  canArchive(route: RouteOverviewViewModel): boolean {
    return (route.status === JobStatus.Finished || route.status === JobStatus.Stopped)
      && !route.archived && this.activeOverviewType !== OverviewType.Archived;
  }

  canActivate(route: RouteOverviewViewModel): boolean {
    return route.archived && this.activeOverviewType === OverviewType.Archived;
  }

  isActiveRoute(route: RouteOverviewViewModel): boolean {
    return route.status === JobStatus.Created || route.status === JobStatus.Scheduled;
  }

  isCreatedNew(route: RouteOverviewViewModel): boolean {
    return !route.createdDate || ((route.status === JobStatus.Created || route.status === JobStatus.Scheduled) && moment(new Date()).diff(route.createdDate, 'days') <= 5);
  }

  noRoutes(): boolean {
    return _.isUndefined(this.routes) || this.routes.length === 0;
  }

  manageAlerts(route: RouteOverviewViewModel): void {
    this.selectedRoute = route;
    this.showAlerts = true;
  }
  isAlertsShown(): boolean {
    return this.showAlerts;
  }
  closeAlerts(): void {
    this.selectedRoute = undefined;
    this.showAlerts = false;
  }

  activateMy(): void {
    this.activateOverviewType(OverviewType.Own);
  }
  activateShared(): void {
    this.activateOverviewType(OverviewType.Shared);
  }
  activateArchive(): void {
    this.activateOverviewType(OverviewType.Archived);
  }

  private activateOverviewType(overviewType: OverviewType) {
    this.activeOverviewType = overviewType;
    this.setCurrentRoutes();
  }

  myRoutesActive(): boolean {
    return this.activeOverviewType === OverviewType.Own;
  }
  sharedRoutesActive(): boolean {
    return this.activeOverviewType === OverviewType.Shared;
  }
  archivedRoutesActive(): boolean {
    return this.activeOverviewType === OverviewType.Archived;
  }

  showRoutes(): boolean {
    return !this.isDeleteShown() && !this.isStopShown() && !this.isAlertsShown() && !this.isSharePopupShown() && !this.isStopSharingPopupShown();
  }

  activateSearchbox(): void {
    this.searchboxActive = true;
    setTimeout(() => {
      this.searchBox.nativeElement.focus();
    }, 0);
  }

  deactivateSearchbox(): void {
    this.searchTerm = '';
    this.searchboxActive = false;
    this.orderRoutesBy(this.routeOverviewService.getLastOrderMethod());
  }

  isSearchboxActive(): boolean {
    return this.searchboxActive;
  }

  filterRoutes(event: any): void {
    if (!this.currentRoutes) { return; }

    const value = event.target.value;
    if (value.length === 0) {
      this.filteredRoutes = this.currentRoutes;
      return;
    }

    this.filteredRoutes = _.filter(this.currentRoutes, (route: RouteOverviewViewModel) => {
      return route.name.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) !== -1;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/route-overview/route-overview.component.ts